import type {
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
} from 'react';
import { Ccontainer } from '../Ccontainer';
import { ContainerPreset } from '@/constants/componentPresets';
import type { DataElScope } from '@/utils/dataEl';
import {
  CSTRETCHMODAL_BOX_SHADOW,
  CSTRETCHMODAL_OVERFLOW_HIDDEN,
} from './Cstretchmodal.constants';
import type { CstretchmodalStyleModel } from './Cstretchmodal.types';
import { CstretchmodalBackButton } from './CstretchmodalBackButton';
import { CstretchmodalContent } from './CstretchmodalContent';
import { CstretchmodalFloatingFooter } from './CstretchmodalFloatingFooter';
import { CstretchmodalFooter } from './CstretchmodalFooter';
import { CstretchmodalHeader } from './CstretchmodalHeader';
import { CstretchmodalOverlayPortal } from './CstretchmodalOverlayPortal';

const WITH_BACK_BUTTON_VALUE = "with-back-button";
const PRIMARY_SURFACE_VALUE = "primary-surface";

interface CstretchmodalSurfaceProps {
  bodyRef?: Ref<HTMLDivElement>;
  canClose: boolean;
  children?: ReactNode;
  className?: string;
  dataElScope: DataElScope;
  floatingFooter: boolean;
  footer?: ReactNode;
  handleClose: () => void;
  header?: ReactNode;
  overlayPortalTargetId?: string;
  showBackButton: boolean;
  stopPropagation: (event?: MouseEvent) => void;
  styleModel: CstretchmodalStyleModel;
  surfaceRef: RefObject<HTMLDivElement | null>;
}

export function CstretchmodalSurface({
  bodyRef,
  canClose,
  children,
  className,
  dataElScope,
  floatingFooter,
  footer,
  handleClose,
  header,
  overlayPortalTargetId,
  showBackButton,
  stopPropagation,
  styleModel,
  surfaceRef,
}: CstretchmodalSurfaceProps): ReactElement {
  return (
    <Ccontainer
      preset={ContainerPreset.Tile}
      padding={styleModel.containerPadding}
      width="auto"
      height="auto"
      overflow={CSTRETCHMODAL_OVERFLOW_HIDDEN}
      display="flex"
      flexDirection="column"
      radius={64}
      boxShadow={CSTRETCHMODAL_BOX_SHADOW}
      className={className}
      rootRef={surfaceRef}
      style={styleModel.surfaceStyle}
      onClick={stopPropagation}
      clickable={false}
    >
      <CstretchmodalHeader dataElScope={dataElScope} header={header} style={styleModel.headerStyle} />
      <CstretchmodalContent bodyRef={bodyRef} dataElScope={dataElScope} style={styleModel.contentStyle}>
        {children}
      </CstretchmodalContent>
      <CstretchmodalFooter dataElScope={dataElScope} floatingFooter={floatingFooter} footer={footer} style={styleModel.footerStyle} />
      <CstretchmodalFloatingFooter
        dataElScope={dataElScope}
        floatingFooter={floatingFooter}
        footer={footer}
        style={styleModel.floatingFooterStyle}
      />
      <CstretchmodalBackButton
        canClose={canClose}
        dataElScope={dataElScope}
        onClick={handleClose}
        showBackButton={showBackButton}
        style={styleModel.backButtonStyle}
      />
      <CstretchmodalOverlayPortal dataElScope={dataElScope} id={overlayPortalTargetId} style={styleModel.overlayPortalStyle} />
    </Ccontainer>
  );
}
